// Mood chips and region matching follow tempovip.com's landing map.
// Do not import cms-public. Source of truth is the CMS landing page
// REGION_MAP block (`cms.pages.findOne` slug `landing/{country}`), with
// parent custom-region labels as the empty-map fallback.

import { titleCaseRegionTitle } from './region-layout';

export const normalizeMood = (tag: string): string =>
  tag.replace(/^#/, '').trim().toLowerCase();

export const displayMood = (tag: string): string =>
  titleCaseRegionTitle(tag.replace(/^#/, '').trim());

export const landingPageSlug = (country: string): string =>
  `landing/${country.trim().toLowerCase().replace(/\s+/g, '-')}`;

export type LandingMapRegion = {
  name: string;
  tags: string[];
  hidden: boolean;
  customRegionIds: string[];
};

type PageBlock = {
  type?: string;
  data?: unknown;
  [key: string]: unknown;
};

type PageLike = {
  content?: Array<{ blocks?: PageBlock[] }>;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];

export const extractLandingMapRegions = (
  page: PageLike | null | undefined
): LandingMapRegion[] => {
  const blocks = page?.content?.[0]?.blocks ?? [];
  for (const raw of blocks) {
    const nested = asRecord(raw.data) ?? {};
    const block = { ...raw, ...nested };
    if (block.type !== 'REGION_MAP') continue;
    const regions = block.regions;
    if (!Array.isArray(regions)) return [];
    return regions.flatMap((entry) => {
      const row = asRecord(entry);
      if (!row) return [];
      return [
        {
          name: typeof row.name === 'string' ? row.name : '',
          tags: asStringArray(row.tags),
          hidden: row.hidden === true,
          customRegionIds: asStringArray(row.customRegionIds),
        },
      ];
    });
  }
  return [];
};

const uniqueMoods = (tags: readonly string[]): string[] => {
  const names = new Set<string>();
  for (const tag of tags) {
    const mood = displayMood(tag);
    if (mood) names.add(mood);
  }
  return [...names].sort((a, b) => a.localeCompare(b));
};

// Unique tags on visible REGION_MAP regions — same list as
// InteractiveRegionMap `derivedTags` on tempovip.com.
export const moodsFromLandingMap = (
  regions: readonly LandingMapRegion[]
): string[] =>
  uniqueMoods(regions.flatMap((region) => (region.hidden ? [] : region.tags)));

type LabeledRegion = {
  id: string;
  title: string;
  parentId: string | null;
  labels: readonly string[];
};

export const moodsFromRegionLabels = (
  regions: readonly LabeledRegion[]
): string[] =>
  uniqueMoods(
    regions.flatMap((region) => (region.parentId === null ? region.labels : []))
  );

// Prefer REGION_MAP tags linked by customRegionIds / name, else labels.
export const labelsForLandingRegion = (
  region: { id: string; title: string; labels: readonly string[] },
  mapRegions: readonly LandingMapRegion[]
): string[] => {
  const nameLc = region.title.trim().toLowerCase();
  const content =
    mapRegions.find((row) => row.customRegionIds[0] === region.id) ??
    mapRegions.find((row) => row.name.trim().toLowerCase() === nameLc);
  if (content && content.tags.length > 0) return content.tags;
  return [...region.labels];
};

type RegionMood = {
  id: string;
  parentId: string | null;
  labels: readonly string[];
};

// Landing tags on the selected parent regions — same input tempovip uses
// for per-section autoselect (`regionTagsByChapter[activeRegion]`).
export const labelsForSelectedParents = (
  regions: readonly LabeledRegion[],
  selectedIds: readonly string[]
): string[] => {
  const selected = new Set(selectedIds);
  return regions.flatMap((region) =>
    region.parentId === null && selected.has(region.id)
      ? [...region.labels]
      : []
  );
};

// Section chips whose name matches a region's landing tags (tempovip).
export const autoSelectForSection = (
  available: readonly string[],
  moods: readonly string[]
): string[] => {
  if (moods.length === 0) return [];
  const moodSet = new Set(moods.map(normalizeMood));
  return available.filter((tag) => moodSet.has(normalizeMood(tag)));
};

export const SECTION_TAG_VISIBLE = 6;

export const pinActiveFirst = (
  available: readonly string[],
  active: readonly string[]
): string[] => {
  if (active.length === 0) return [...available];
  const activeSet = new Set(active);
  return [...active, ...available.filter((tag) => !activeSet.has(tag))];
};

export const visibleSectionTags = (
  available: readonly string[],
  active: readonly string[],
  limit = SECTION_TAG_VISIBLE
): string[] => pinActiveFirst(available, active).slice(0, limit);

export const regionsMatchingMoods = (
  regions: readonly RegionMood[],
  moods: readonly string[]
): string[] => {
  if (moods.length === 0) return [];
  const moodSet = new Set(moods.map(normalizeMood));
  return regions
    .filter(
      (region) =>
        region.parentId === null &&
        region.labels.length > 0 &&
        region.labels.some((label) => moodSet.has(normalizeMood(label)))
    )
    .map((region) => region.id);
};

// Product filter — CMS `deriveActiveRegionIds` without sub-chapters
// (advisor chips are parent-only). Empty → country-wide.
export const deriveActiveRegionIds = (
  selectedIds: readonly string[],
  activeId: string | null
): string[] => (activeId && selectedIds.includes(activeId) ? [activeId] : []);

// Map click / unlisted chip: add to the shortlist if needed and make it
// the sole catalogue filter. Never removes.
export const selectChapter = (
  selectedIds: readonly string[],
  chapterId: string
): { region: string[]; active: string } => ({
  region: selectedIds.includes(chapterId)
    ? [...selectedIds]
    : [...selectedIds, chapterId],
  active: chapterId,
});

// Sticky-chip click: toggle this chapter as the active filter. List unchanged.
export const setActiveChapter = (
  selectedIds: readonly string[],
  activeId: string | null,
  chapterId: string
): { region: string[]; active: string | null } => ({
  region: [...selectedIds],
  active: activeId === chapterId ? null : chapterId,
});

// Chip × / map deselect: drop from the shortlist; clear active if it was this one.
export const removeChapter = (
  selectedIds: readonly string[],
  activeId: string | null,
  chapterId: string
): { region: string[]; active: string | null } => {
  const region = selectedIds.filter((id) => id !== chapterId);
  return {
    region,
    active: activeId === chapterId ? null : activeId,
  };
};

export const unionIds = (
  left: readonly string[],
  right: readonly string[]
): string[] => [...new Set([...left, ...right])];

export const sameIdList = (
  left: readonly string[],
  right: readonly string[]
): boolean =>
  left.length === right.length && left.every((id) => right.includes(id));
